import React from 'react'
import { BaseRouteParams, withNavigation, WithNavigationProps } from 'hocs'
import { usePermission } from 'admin/hocs'
import { InlineTimePicker } from 'uiComponents/input'
import { IOrder } from 'orders/schema'
import { useUpdateVisitDateAndTimeMutation } from 'orders/reduxQueries'
import { addMessage } from 'uiComponents/messages/actions'
import { useDispatch } from 'react-redux'
import { convertDateFormat, convertTimeFormat } from 'utils/dates'
import { useTranslation } from 'translations'

interface InlineVisitTimeProps extends WithNavigationProps<BaseRouteParams> {
    order: IOrder
    onUpdated?: (order: IOrder) => void
}

function InlineVisitTime({ order, match, onUpdated }: InlineVisitTimeProps) {
    const { t } = useTranslation()
    const { accountSlug } = match.params
    const [updateDateTime] = useUpdateVisitDateAndTimeMutation()
    const dispatch = useDispatch()
    const { hasPermission } = usePermission()
    
    const canEditOrders =
        hasPermission('edit_orders', accountSlug) && order.status !== 'refunded' && order.status !== 'refunding'

    const saveTime = async (time: string) => {
        try {
            await updateDateTime({
                orderUuid: order.id,
                dateTime: null,
                visitTime: time || null,
            })
            if (onUpdated && time) {
                onUpdated({ ...order, visitTime: time })
            }
        } catch (e) {
            if (e.message === 'no_availability') {
                dispatch(
                    addMessage('no_availability_error', 'error', t('orders.orders.inlineVisitTime.noAvailability')),
                )
            } else if (e.message === 'order_already_redeemed') {
                dispatch(
                    addMessage('already_redeemed_error', 'error', t('orders.orders.inlineVisitTime.alreadyRedeemed')),
                )
            } else if (e.message === 'date_change_not_possible') {
                dispatch(
                    addMessage(
                        'date_change_not_possible',
                        'error',
                        t('orders.orders.inlineVisitTime.changeNotPossible'),
                    ),
                )
            } else {
                dispatch(addMessage('visit_date_error', 'error', t('orders.orders.inlineVisitTime.saveError')))
            }
        }
    }

    if (!order.visitTime) {
        return <>{t('orders.orders.inlineVisitTime.anyTime')}</>
    }

    return (
        <InlineTimePicker
            id="time"
            key={order.id}
            value={convertDateFormat(order.visitTime, 'HH:mm:ss', 'HH:mm')}
            onSave={saveTime}
            disabled={!canEditOrders}
            requireConfirmation={true}
            confirmationMessage={(time) =>
                t('orders.orders.inlineVisitTime.confirmAvailability', { time: convertTimeFormat(time!) })
            }
            required
            style={{ width: '100px' }}
        />
    )
}

export default withNavigation(InlineVisitTime)
